import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Text } from '../components/ui';
import { getFriendsPosts, getFriends } from '../services/api';
import { useAuth } from '../context/authContext';
import FriendsModal from '../components/FriendsModal';
import PlaceSetting from '../components/PlaceSetting';
import SessionDetailModal from '../components/SessionDetailModal';
import FriendHistoryModal from '../components/FriendHistoryModal';
import TableIcons from '../components/TableIcons';
import FloatingCreateButton from '../components/FloatingCreateButton';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

const TheTable = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [tableSettings, setTableSettings] = useState([]);
    const [friendsCount, setFriendsCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFriendsModal, setShowFriendsModal] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [selectedFriend, setSelectedFriend] = useState(null);
    const [showSessionModal, setShowSessionModal] = useState(false);
    const [showFriendHistoryModal, setShowFriendHistoryModal] = useState(false);
    const [hoveredFriend, setHoveredFriend] = useState(null);
    const [hoveredPost, setHoveredPost] = useState(null);
    
    // Set default to first friend with a session
    useEffect(() => {
        if (tableSettings.length > 0 && !hoveredFriend) {
            const firstFriendWithSession = tableSettings.find(setting => setting.latestPost);
            if (firstFriendWithSession) {
                setHoveredFriend(firstFriendWithSession.friend);
                setHoveredPost(firstFriendWithSession.latestPost);
            }
        }
    }, [tableSettings, hoveredFriend]);

    useEffect(() => {
        if (user) {
            fetchData();
        }
    }, [user]);

    const fetchData = async () => {
        if (!user) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const token = await user.getIdToken();
            
            // Fetch friends and posts in parallel
            const [friendsData, postsData] = await Promise.all([
                getFriends(token),
                getFriendsPosts({ limit: 100 }, token)
            ]);
            
            // Group posts by user_id, take most recent per friend
            const latestPostsByFriend = {};
            (postsData.posts || []).forEach(post => {
                const userId = post.user_id;
                if (!latestPostsByFriend[userId] || 
                    new Date(post.posted_at) > new Date(latestPostsByFriend[userId].posted_at)) {
                    latestPostsByFriend[userId] = post;
                }
            });
            
            // Merge friends with their latest post
            // Sort by: friends with posts first (by most recent post), then friends without posts
            const settings = friendsData.map(friend => ({
                friend,
                latestPost: latestPostsByFriend[friend.id] || null
            }));
            
            // Sort by latest session date (most recent first)
            settings.sort((a, b) => {
                if (a.latestPost && !b.latestPost) return -1;
                if (!a.latestPost && b.latestPost) return 1;
                if (a.latestPost && b.latestPost) {
                    return new Date(b.latestPost.posted_at) - new Date(a.latestPost.posted_at);
                }
                // If no posts, sort alphabetically by name
                const nameA = a.friend.display_name || a.friend.username || '';
                const nameB = b.friend.display_name || b.friend.username || '';
                return nameA.localeCompare(nameB);
            });
            
            setFriendsCount(friendsData.length);
            setTableSettings(settings);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load table data');
        } finally {
            setLoading(false);
        }
    };

    const handlePlateClick = (post) => {
        if (post) {
            setSelectedPost(post);
            setShowSessionModal(true);
        }
    };

    const handleNameClick = (friend) => {
        setSelectedFriend(friend);
        setShowFriendHistoryModal(true);
    };

    const handleHover = (friend, post) => {
        // Only update if friend has a post
        if (post) {
            setHoveredFriend(friend);
            setHoveredPost(post);
        }
    };

    // Get image from post
    const getPlateImage = (post) => {
        if (!post) return null;
        
        // Try final image first
        if (post.final_image?.url) {
            return post.final_image.url;
        }
        
        // Try first progress photo
        if (post.progress_photos && post.progress_photos.length > 0 && post.progress_photos[0]?.url) {
            return post.progress_photos[0].url;
        }
        
        // Try recipe images
        if (post.recipe_images && Array.isArray(post.recipe_images) && post.recipe_images.length > 0) {
            const firstImage = post.recipe_images[0];
            if (typeof firstImage === 'string') {
                return firstImage;
            }
        }
        
        return null;
    };

    const currentPlateImage = getPlateImage(hoveredPost);
    const currentRecipeTitle = hoveredPost?.recipe_title || '';
    const currentFriendName = hoveredFriend?.display_name || hoveredFriend?.username || '';

    if (!user) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="page-heading-container hairline min-h-[32vh] md:min-h-[48vh] relative">
                        <div className="page-heading-content absolute bottom-0 left-2 right-2 md:left-4 md:right-4">
                            <h1 className="font-sans text-hero text-neutral-900 tracking-tight font-medium">
                                THE TABLE
                            </h1>
                            <p className="page-heading-subtitle font-mono uppercase tracking-wider text-neutral-900 opacity-60 mt-0 text-sm md:text-lg">
                                FRIENDS & FAMILY
                            </p>
                        </div>
                    </div>
                    <div className="bg-neutral-0 hairline minimal-padding vspace">
                        <div className="text-center py-12">
                            <Text className="text-lg leading-relaxed max-w-2xl mx-auto">
                                Please log in to see your friends' cooking activity.
                            </Text>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50">
            {/* Floating Create Button */}
            {user && <FloatingCreateButton />}
            
            <div className="w-full px-0 py-8">
                {/* Header */}
                <div className="page-heading-container hairline min-h-[32vh] md:min-h-[48vh] relative" style={{ backgroundColor: colorTokens.white }}>
                    {/* Manage Friends Button - Bottom Right of Heading */}
                    <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4 z-10">
                        <button
                            onClick={() => setShowFriendsModal(true)}
                            className="px-3 h-10 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors font-mono text-xs uppercase tracking-wider flex items-center"
                        >
                            MANAGE FRIENDS
                        </button>
                    </div>
                    
                    <div className="page-heading-content absolute bottom-0 left-2 right-2 md:left-4 md:right-4">
                        <h1 className="font-sans text-hero text-neutral-900 tracking-tight font-medium">
                            THE TABLE
                        </h1>
                        <p className="page-heading-subtitle font-mono uppercase tracking-wider text-neutral-900 opacity-60 mt-0 text-sm md:text-lg">
                            {friendsCount === 0 ? 'NO FRIENDS YET' : `${friendsCount} ${friendsCount === 1 ? 'FRIEND' : 'FRIENDS'} AT THE TABLE`}
                        </p>
                    </div>
                </div>

                {/* Outer Container - White Background */}
                <div 
                    className="bg-neutral-0 hairline flex"
                    style={{ backgroundColor: colorTokens.white }}
                >
                    {loading ? (
                        <div className="text-center py-12 w-full">
                            <Text className="opacity-60">Setting the table...</Text>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 w-full">
                            <Text className="text-red-600">{error}</Text>
                        </div>
                    ) : friendsCount === 0 ? (
                        <div className="text-center py-16 space-y-6 w-full">
                            <div className="max-w-md mx-auto">
                                <Text className="text-lg leading-relaxed opacity-60 mb-2">
                                    The table is empty
                                </Text>
                                <Text className="text-sm opacity-60 mb-6">
                                    Add friends to see their latest cooking sessions here!
                                </Text>
                                <button
                                    onClick={() => setShowFriendsModal(true)}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-all"
                                    style={{ backgroundColor: colorTokens.oxfordBlue, color: colorTokens.white }}
                                >
                                    ADD FRIENDS
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Friends List - Left Side */}
                            <div className="w-1/3">
                                <div className="h-96 overflow-y-auto">
                                    {tableSettings.map((setting, index) => (
                                        <PlaceSetting
                                            key={setting.friend.id || `friend-${index}`}
                                            friend={setting.friend}
                                            post={setting.latestPost}
                                            onNameClick={handleNameClick}
                                            onHover={handleHover}
                                            isHovered={hoveredFriend?.id === setting.friend.id}
                                        />
                                    ))}
                                </div>
                            </div>
                            
                            {/* Black Divider */}
                            <div className="w-px bg-neutral-900"></div>
                            
                            {/* Large Photo - Right Side */}
                            {currentPlateImage ? (
                                <img 
                                    src={currentPlateImage}
                                    alt={currentRecipeTitle}
                                    className="w-2/3 h-96 object-cover cursor-pointer"
                                    onClick={() => hoveredPost && handlePlateClick(hoveredPost)}
                                />
                            ) : (
                                <div 
                                    className="w-2/3 h-96 flex items-center justify-center cursor-pointer"
                                    style={{ backgroundColor: colorTokens.neutral[50] }}
                                    onClick={() => hoveredPost && handlePlateClick(hoveredPost)}
                                >
                                    <Text className="font-mono text-sm text-neutral-900 opacity-60">
                                        No image available
                                    </Text>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Modals */}
            <FriendsModal
                isOpen={showFriendsModal}
                onClose={() => {
                    setShowFriendsModal(false);
                    fetchData();
                }}
            />
            
            <SessionDetailModal
                isOpen={showSessionModal}
                onClose={() => {
                    setShowSessionModal(false);
                    setSelectedPost(null);
                }}
                post={selectedPost}
            />
            
            <FriendHistoryModal
                isOpen={showFriendHistoryModal}
                onClose={() => {
                    setShowFriendHistoryModal(false);
                    setSelectedFriend(null);
                }}
                friend={selectedFriend}
                onPostClick={handlePlateClick}
            />
        </div>
    );
};

export default TheTable;
